import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  HeartIcon,
  ChatBubbleLeftIcon,
  ShareIcon,
  BookmarkIcon,
  EllipsisHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  PaperAirplaneIcon,
  FaceSmileIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid, BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid'

const StoryDetailPage: React.FC = () => {
  const [currentChapter, setCurrentChapter] = useState(1)
  const [isLiked, setIsLiked] = useState(false)
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [showComments, setShowComments] = useState(true)
  const [comment, setComment] = useState('')
  const [reactions, setReactions] = useState({
    likes: 1420,
    loved: 892,
    fire: 234,
    clap: 567
  })

  const totalChapters = 12
  const story = {
    title: 'Summer Adventures in Tokyo',
    author: 'Sarah Anderson',
    authorAvatar: '',
    category: 'Travel',
    views: 8500,
    published: '2 days ago'
  }

  const comments = [
    {
      id: 1,
      user: 'Mike Johnson',
      avatar: '',
      text: 'This is absolutely amazing! I love how you captured the essence of Tokyo.',
      time: '2 hours ago',
      likes: 24
    },
    {
      id: 2,
      user: 'Emma Davis',
      avatar: '',
      text: 'Your storytelling is incredible. Can\'t wait for the next chapter!',
      time: '5 hours ago',
      likes: 18
    },
    {
      id: 3,
      user: 'Alex Chen',
      avatar: '',
      text: 'The way you describe the streets of Shibuya is so vivid. I feel like I\'m there!',
      time: '1 day ago',
      likes: 32
    }
  ]

  const handleReaction = (type: keyof typeof reactions) => {
    setReactions(prev => ({
      ...prev,
      [type]: prev[type] + 1
    }))
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Story Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-glass p-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600" />
            <div>
              <h3 className="font-bold text-lg">{story.author}</h3>
              <p className="text-sm text-white/70">Published {story.published}</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-glass px-4 py-2"
          >
            + Follow
          </motion.button>
        </div>

        <h1 className="text-4xl font-display font-bold mb-4">{story.title}</h1>
        
        <div className="flex items-center space-x-6 text-sm text-white/70 mb-6">
          <span className="px-3 py-1 bg-primary-500/20 text-primary-300 rounded-full">
            {story.category}
          </span>
          <span>{story.views.toLocaleString()} views</span>
          <span>Chapter {currentChapter} of {totalChapters}</span>
        </div>

        {/* Chapter Navigation */}
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setCurrentChapter(Math.max(1, currentChapter - 1))}
            disabled={currentChapter === 1}
            className="btn-glass p-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </motion.button>
          
          <div className="flex-1 bg-white/10 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-primary-600 to-secondary-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(currentChapter / totalChapters) * 100}%` }}
            />
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setCurrentChapter(Math.min(totalChapters, currentChapter + 1))}
            disabled={currentChapter === totalChapters}
            className="btn-glass p-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </motion.button>
        </div>
      </motion.div>

      {/* Chapter Content */}
      <motion.div
        key={currentChapter}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="card-glass p-8"
      >
        <h2 className="text-2xl font-bold mb-6">Chapter {currentChapter}: The Beginning</h2>
        
        {/* Featured Image/Video */}
        <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mb-6">
          <div className="absolute inset-0 flex items-center justify-center">
            <PlayIcon className="h-16 w-16 text-white/30" />
          </div>
        </div>

        {/* Story Content */}
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-white/80 mb-4">
            The neon lights of Shibuya crossing painted the night sky in a kaleidoscope of colors. 
            I stood there, mesmerized by the organized chaos of thousands of people crossing the intersection 
            in perfect harmony. This was my first night in Tokyo, and already I knew this journey would 
            change everything.
          </p>
          <p className="text-lg leading-relaxed text-white/80 mb-4">
            The humid summer air carried the scent of street food from nearby vendors. Takoyaki, yakitori, 
            and the sweet aroma of taiyaki filled my senses. I had dreamed of this moment for years, and 
            now, standing in the heart of one of the world's most vibrant cities, I felt truly alive.
          </p>
          <p className="text-lg leading-relaxed text-white/80 mb-4">
            My phone buzzed with a message from Yuki, my local guide I'd met through a travel app. 
            "Welcome to Tokyo! Ready for an adventure?" Her message was followed by an address in Harajuku. 
            Little did I know, this was just the beginning of a summer that would redefine my understanding 
            of connection, culture, and myself.
          </p>
        </div>
      </motion.div>

      {/* Reactions Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card-glass p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsLiked(!isLiked)}
              className="flex items-center space-x-2"
            >
              {isLiked ? (
                <HeartSolid className="h-6 w-6 text-red-500" />
              ) : (
                <HeartIcon className="h-6 w-6" />
              )}
              <span className="font-medium">{reactions.likes}</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-2"
            >
              <ChatBubbleLeftIcon className="h-6 w-6" />
              <span className="font-medium">{comments.length}</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="flex items-center space-x-2"
            >
              <ShareIcon className="h-6 w-6" />
              <span className="font-medium">Share</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsBookmarked(!isBookmarked)}
            >
              {isBookmarked ? (
                <BookmarkSolid className="h-6 w-6 text-primary-400" />
              ) : (
                <BookmarkIcon className="h-6 w-6" />
              )}
            </motion.button>
          </div>

          <div className="flex items-center space-x-2">
            <ReactionButton emoji="❤️" count={reactions.loved} onClick={() => handleReaction('loved')} />
            <ReactionButton emoji="🔥" count={reactions.fire} onClick={() => handleReaction('fire')} />
            <ReactionButton emoji="👏" count={reactions.clap} onClick={() => handleReaction('clap')} />
          </div>
        </div>
      </motion.div>

      {/* Comments Section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card-glass p-6 space-y-6"
          >
            <h3 className="text-xl font-bold">Comments ({comments.length})</h3>

            {/* Comment Input */}
            <div className="flex space-x-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 flex-shrink-0" />
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="input-glass w-full pr-20"
                  />
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                    <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                      <FaceSmileIcon className="h-5 w-5" />
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-2 bg-primary-600 rounded-lg"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>

            {/* Comments List */}
            <div className="space-y-4">
              {comments.map((comment, index) => (
                <CommentCard key={comment.id} comment={comment} index={index} />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="btn-glass w-full"
            >
              Load More Comments
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Related Stories */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-4"
      >
        <h3 className="text-2xl font-bold">More from {story.author}</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <RelatedStoryCard
            title="Winter in Hokkaido"
            chapters={8}
            views={5200}
          />
          <RelatedStoryCard
            title="Spring Sakura Season"
            chapters={10}
            views={7800}
          />
        </div>
      </motion.div>
    </div>
  )
}

interface ReactionButtonProps {
  emoji: string
  count: number
  onClick: () => void
}

const ReactionButton: React.FC<ReactionButtonProps> = ({ emoji, count, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className="flex items-center space-x-1 px-3 py-1 bg-white/5 rounded-full hover:bg-white/10 transition-colors"
  >
    <span className="text-lg">{emoji}</span>
    <span className="text-sm font-medium">{count}</span>
  </motion.button>
)

interface CommentCardProps {
  comment: any
  index: number
}

const CommentCard: React.FC<CommentCardProps> = ({ comment, index }) => {
  const [liked, setLiked] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="flex space-x-4 p-4 bg-white/5 rounded-xl"
    >
      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 flex-shrink-0" />
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-bold">{comment.user}</h4>
            <p className="text-xs text-white/50">{comment.time}</p>
          </div>
          <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <EllipsisHorizontalIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-white/80 mb-3">{comment.text}</p>
        <div className="flex items-center space-x-4 text-sm">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setLiked(!liked)}
            className="flex items-center space-x-1"
          >
            {liked ? (
              <HeartSolid className="h-4 w-4 text-red-500" />
            ) : (
              <HeartIcon className="h-4 w-4" />
            )}
            <span>{comment.likes + (liked ? 1 : 0)}</span>
          </motion.button>
          <button className="text-white/70 hover:text-white">Reply</button>
        </div>
      </div>
    </motion.div>
  )
}

interface RelatedStoryCardProps {
  title: string
  chapters: number
  views: number
}

const RelatedStoryCard: React.FC<RelatedStoryCardProps> = ({ title, chapters, views }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -4 }}
    className="card-glass p-6 cursor-pointer"
  >
    <div className="aspect-video rounded-xl bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mb-4 flex items-center justify-center">
      <PlayIcon className="h-12 w-12 text-white/30" />
    </div>
    <h4 className="font-bold text-lg mb-2">{title}</h4>
    <div className="flex items-center space-x-4 text-sm text-white/70">
      <span>{chapters} chapters</span>
      <span>•</span>
      <span>{views.toLocaleString()} views</span>
    </div>
  </motion.div>
)

export default StoryDetailPage
